import { apiClient } from '@/shared/lib/apiClient';
import type {
  ApiResponse,
  CommercialAgreement,
  FinanceAnalytics,
  FinanceReportSummary,
  GuideBankAccount,
  GuideWallet,
  PaginatedResponse,
  Settlement,
  WalletTransaction,
  WithdrawalRequest,
} from '@/types';

export const adminFinanceApi = {
  async getSummary() {
    const res = await apiClient.get<ApiResponse<FinanceReportSummary>>('/admin/finance/reports/summary');
    return res.data.data!;
  },

  async getAnalytics(params?: { days?: number }) {
    const res = await apiClient.get<ApiResponse<FinanceAnalytics>>('/admin/finance/reports/analytics', {
      params,
    });
    return res.data.data!;
  },

  async listCommercial(params?: { page?: number; limit?: number; guideId?: string }) {
    const res = await apiClient.get<PaginatedResponse<CommercialAgreement>>(
      '/admin/finance/commercial-agreements',
      { params }
    );
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async createCommercial(payload: {
    guideId: string;
    commissionType: string;
    commissionValue: number;
    effectiveFrom: string;
    effectiveTo?: string;
  }) {
    const res = await apiClient.post<ApiResponse<CommercialAgreement>>(
      '/admin/finance/commercial-agreements',
      payload
    );
    return res.data.data!;
  },

  async listSettlements(params?: { page?: number; limit?: number; guideId?: string; status?: string }) {
    const res = await apiClient.get<PaginatedResponse<Settlement>>('/admin/finance/settlements', {
      params,
    });
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async listWallets(params?: { page?: number; limit?: number }) {
    const res = await apiClient.get<PaginatedResponse<GuideWallet>>('/admin/finance/wallets', { params });
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async getWalletByGuide(guideId: string) {
    const res = await apiClient.get<
      ApiResponse<{ wallet: GuideWallet; recentTransactions: WalletTransaction[] }>
    >(`/admin/finance/wallets/${guideId}`);
    return res.data.data!;
  },

  async adjustWallet(
    guideId: string,
    payload: { amount: number; transactionType: string; reason: string }
  ) {
    const res = await apiClient.post<ApiResponse<GuideWallet>>(
      `/admin/finance/wallets/${guideId}/adjustments`,
      payload
    );
    return res.data.data!;
  },

  async listWithdrawals(params?: { page?: number; limit?: number; status?: string; guideId?: string }) {
    const res = await apiClient.get<PaginatedResponse<WithdrawalRequest>>('/admin/finance/withdrawals', {
      params,
    });
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async approveWithdrawal(id: string, adminRemarks?: string) {
    const res = await apiClient.patch<ApiResponse<WithdrawalRequest>>(
      `/admin/finance/withdrawals/${id}/approve`,
      { adminRemarks }
    );
    return res.data.data!;
  },

  async rejectWithdrawal(id: string, adminRemarks?: string) {
    const res = await apiClient.patch<ApiResponse<WithdrawalRequest>>(
      `/admin/finance/withdrawals/${id}/reject`,
      { adminRemarks }
    );
    return res.data.data!;
  },

  async completeWithdrawal(id: string) {
    const res = await apiClient.patch<ApiResponse<WithdrawalRequest>>(
      `/admin/finance/withdrawals/${id}/complete`
    );
    return res.data.data!;
  },

  async listPendingBankAccounts(params?: { page?: number; limit?: number }) {
    const res = await apiClient.get<PaginatedResponse<GuideBankAccount>>(
      '/admin/finance/bank-accounts/pending',
      { params }
    );
    return { data: res.data.data, pagination: res.data.pagination };
  },

  async verifyBankAccount(id: string) {
    await apiClient.patch(`/admin/finance/bank-accounts/${id}/verify`);
  },
};

export const adminFinanceKeys = {
  all: ['admin-finance'] as const,
  summary: () => [...adminFinanceKeys.all, 'summary'] as const,
  analytics: (params?: object) => [...adminFinanceKeys.all, 'analytics', params] as const,
  commercial: (params?: object) => [...adminFinanceKeys.all, 'commercial', params] as const,
  settlements: (params?: object) => [...adminFinanceKeys.all, 'settlements', params] as const,
  wallets: (params?: object) => [...adminFinanceKeys.all, 'wallets', params] as const,
  withdrawals: (params?: object) => [...adminFinanceKeys.all, 'withdrawals', params] as const,
  pendingBanks: (params?: object) => [...adminFinanceKeys.all, 'pending-banks', params] as const,
};
